import type { ReactNode } from 'react';
import type { ProjectPageLoader } from '@modules/project/type';

export const ACHIPRO_PHOTOGRAPHER_SPLIT = '[__ACHIPRO_PHOTOGRAPHER_SPLIT__]';

// Collective/mass-noun category titles that have no sensible singular form, so
// they display verbatim (e.g. "Earthworks", not "Earthwork").
const VERBATIM_TITLES = new Set([
    'earthworks',
    'civil works',
    'demolition works',
    'commercial interior fitouts',
]);

// Words ending in "s" that are already singular or mass nouns, so the
// trailing-"s" rule must leave them alone (otherwise "Gas" becomes "Ga").
const NON_PLURAL_WORDS = new Set(['gas', 'electronics']);

const singularizeWord = (word: string): string => {
    if (NON_PLURAL_WORDS.has(word.toLowerCase())) {
        return word;
    }
    // Sibilant plurals (boxes, churches, finishes) drop the whole "es".
    if (/(xes|zes|ches|shes)$/i.test(word)) {
        return word.slice(0, -2);
    }
    if (/ies$/i.test(word)) {
        return word.slice(0, -3) + 'y';
    }
    // Singular words ending in a double "s" (glass, business) are not plurals.
    if (/ss$/i.test(word)) {
        return word;
    }
    if (/s$/i.test(word)) {
        return word.slice(0, -1);
    }
    return word;
};

// Singularises every word so compound titles ("Plumbers & Gas Fitters") read as
// a role rather than a plural list. Suffix rules can't cover every English case,
// so the two sets above carry the exceptions. See the unit tests for the matrix.
export const singularizeString = (text: string): string => {
    if (VERBATIM_TITLES.has(text.trim().toLowerCase())) {
        return text;
    }
    return text.split(' ').map(singularizeWord).join(' ');
};

interface DetailsItems {
    itemName: string;
    itemNameDesktop?: string;
    value: string;
    link?: string;
    isExternal?: boolean;
}

interface Photographer {
    ID?: number;
    Title?: string | null;
    Link?: string | null;
    Website?: string | null;
    InLibrary?: boolean | null;
}

export type DetailsItemsData = Partial<
    Pick<
        ProjectPageLoader['project'],
        | 'Title'
        | 'Professional'
        | 'ProjectRoles'
        | 'Categories'
        | 'Location'
        | 'YearOfCompletion'
        | 'ProjectPriceRange'
        | 'BuildingStyle'
        | 'Client'
        | 'LibraryDownloads'
        | 'Awards'
    > & { Photographers?: Photographer[] | null }
>;

const isNonBlank = (value?: string | null): boolean =>
    (value ?? '').trim().length > 0;

const truncateText = (text: string) => {
    if (!text || text.length <= 35) {
        return text ?? '';
    }
    let wordLength = 0;
    const textArray = text.split(' ')?.map((word, index) => {
        wordLength += word.length + 1;
        return {
            word,
            length: word.length,
            wordLength,
            index,
        };
    });

    const sortedTextArray = [...textArray].sort(
        (a, b) => b.wordLength - a.wordLength,
    );
    const index =
        sortedTextArray.find((item) => item.wordLength <= 35)?.index || 0;

    return (
        textArray
            .slice(0, index + 1)
            .map((item) => item.word)
            .join(' ') + '...'
    );
};

export const getDetailsItems = (
    data: DetailsItemsData,
    {
        includeAddress = false,
        includeDownloads = true,
        includeNewFields = true,
    }: {
        includeAddress?: boolean;
        includeDownloads?: boolean;
        includeNewFields?: boolean;
    },
): DetailsItems[] => {
    const detailsItems = [];

    if (data.Title && includeNewFields) {
        detailsItems.push({
            itemName: 'Title',
            value: data.Title,
        });
    }

    if (data.Professional && includeNewFields) {
        const roleTitles = (data.ProjectRoles ?? [])
            .map((role) => role?.PrettyTitle)
            .filter(isNonBlank) as string[];
        const fallbackCategoryTitle =
            data.Professional.Categories?.[0]?.PrettyTitle;

        // Newline-separated so multiple roles stack one per line in the
        // renderers that show them (they apply `whitespace-pre-line`).
        const roleLabel =
            roleTitles.length > 0
                ? roleTitles.map(singularizeString).join('\n')
                : fallbackCategoryTitle
                  ? singularizeString(fallbackCategoryTitle)
                  : 'Professional';

        detailsItems.push({
            itemName: roleLabel,
            value: data.Professional.Title,
            link: data.Professional.Link,
        });
    }

    if (data.Categories && data.Categories.length !== 0 && includeNewFields) {
        const category = data.Categories[0];

        if (category) {
            const parents =
                category.Parents?.filter((parent) => !!parent).map(
                    (parent) => parent?.PrettyTitle || '',
                ) || [];
            const self = category.PrettyTitle;
            parents.push(self);

            const links = category.Parents?.map((parent) => parent?.Link) || [];
            links.push(category.Link);

            detailsItems.push({
                itemName: 'Category',
                value: parents.join(),
                link: links.join(),
            });
        }
    }

    if (includeAddress && data.Location) {
        // Don't show "Region" when we only have a country — "Region: NZ" is not meaningful.
        const hasRegionOrSuburb =
            isNonBlank(data.Location.Region) ||
            isNonBlank(data.Location.Suburb);

        if (hasRegionOrSuburb) {
            detailsItems.push({
                itemName: 'Region',
                value: [
                    data.Location.Suburb,
                    data.Location.Region,
                    data.Location.Country,
                ]
                    .filter(isNonBlank)
                    .join(', '),
            });
        }
    }

    if (data.YearOfCompletion && data.YearOfCompletion !== 0) {
        detailsItems.push({
            itemName: 'Year of completion',
            itemNameDesktop: 'Completed',
            value: String(data.YearOfCompletion),
        });
    }

    if (data.ProjectPriceRange) {
        detailsItems.push({
            itemName: 'Price range to build',
            itemNameDesktop: 'Price range',
            value: data.ProjectPriceRange,
        });
    }

    if (data.BuildingStyle) {
        detailsItems.push({
            itemName: 'Building style',
            value: data.BuildingStyle.replaceAll('_', ' '),
        });
    }

    if (data.Client) {
        detailsItems.push({
            itemName: 'Client',
            value: data.Client,
        });
    }

    if (data.Awards && data.Awards.length !== 0) {
        data.Awards.forEach((award, index) => {
            detailsItems.push({
                itemName: index === 0 ? 'Awards' : '',
                value: `${award.title} ${award.year ? `- ${award.year}` : ''}`,
            });
        });
    }

    if (
        data.Photographers &&
        data.Photographers.length !== 0 &&
        includeNewFields
    ) {
        const photographers = data.Photographers.map(
            (photographer: Photographer) => {
                const title = photographer.Title || '';
                title.replaceAll(ACHIPRO_PHOTOGRAPHER_SPLIT, '');
                return truncateText(title);
            },
        );
        const links = data.Photographers.map((photographer: Photographer) => {
            // Only provide link if photographer is live (InLibrary = true)
            if (!photographer.InLibrary) {
                return '';
            }
            const link = photographer.Link || photographer.Website || '';
            link.replaceAll(ACHIPRO_PHOTOGRAPHER_SPLIT, '');
            return link;
        });

        detailsItems.push({
            itemName: 'Photographers',
            value: photographers.join(ACHIPRO_PHOTOGRAPHER_SPLIT),
            link: links.join(ACHIPRO_PHOTOGRAPHER_SPLIT),
        });
    }

    if (
        includeDownloads &&
        data.LibraryDownloads &&
        data.LibraryDownloads.length !== 0
    ) {
        const fileCategories = new Map<string, boolean>();
        data.LibraryDownloads.forEach((item) => {
            const categoryName = item.FileCategory?.Title ?? 'Plans';
            const label = fileCategories.has(categoryName) ? '' : categoryName;
            fileCategories.set(categoryName, true);

            detailsItems.push({
                itemName: label,
                value: item.Title,
                link: item.InternalLink || item.ExternalLink,
                isExternal: !!item.ExternalLink,
            });
        });
    }

    return detailsItems;
};

export const displayItems = (
    items: DetailsItems[],
    showItem: (
        label: string,
        value: string,
        link?: string,
        isExternal?: boolean,
    ) => ReactNode,
) => {
    return items.map((item) => {
        return showItem(item.itemName, item.value, item.link, item.isExternal);
    });
};
